import React, { useCallback, useMemo, useState } from 'react';
import { useDisclosure } from '@withjoy/joykit';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useProductOfferings } from '../ConciergeServiceSection/useProductOfferings';
import { useGalleryPlacementSeed } from './useGalleryPlacementSeed';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { ProductOffering } from '../ConciergeServiceSection/ConciergeServiceSection.types';
import { isConciergeServiceCategory } from '@apps/card/routes/Concierge/config';
import { ProductOfferingDialog } from '../ConciergeServiceSection/ProductOfferingDialog';
import type { CustomizeNowClickOverrideHandler } from './DesignsGallery.types';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import type { CardEventType } from './components/GalleryItem/GalleryItem';

interface SelectedOfferingState {
  readonly selectedOfferingSortIndex: number | undefined;
  readonly selectedOffering: ProductOffering | undefined;
}

export const useConciergeDisclosure = (onCustomizeCardClickOverride?: CustomizeNowClickOverrideHandler) => {
  const { isOpen, onOpen: openDisclosure, onClose: closeDisclosure } = useDisclosure();
  const { currentCategory } = useCardCategoryContext();
  const { goToConcierge } = useCardsRouterContext();
  // Same seed as the gallery's placement, so the sort index reported is the displayed one.
  const { productOfferings } = useProductOfferings({ seed: useGalleryPlacementSeed() });
  const [selectedOffering, setSelectedOffering] = useState<SelectedOfferingState>({
    selectedOfferingSortIndex: undefined,
    selectedOffering: undefined,
  });

  const getSelectedOffering = useCallback(
    (id: string, category: StationeryTemplateCategoryEnum): SelectedOfferingState => {
      if (!isConciergeServiceCategory(category)) {
        return { selectedOfferingSortIndex: -1, selectedOffering: undefined };
      }
      const sortIndex = productOfferings[category].findIndex(offering => offering.id === id);
      return {
        selectedOfferingSortIndex: sortIndex,
        selectedOffering: productOfferings[category][sortIndex],
      };
    },
    [productOfferings],
  );

  const onCardSelect = useEventCallback(
    (event: CardEventType) => {
      if (event.type === 'conciergeselectedevent') {
        setSelectedOffering(getSelectedOffering(event.id, currentCategory));
        openDisclosure();
      }
    },
    [getSelectedOffering, currentCategory, openDisclosure],
  );

  const onCustomizeClick = useEventCallback((offering: ProductOffering) => {
    if (onCustomizeCardClickOverride) {
      onCustomizeCardClickOverride(
        {
          format: 'concierge',
          template: { ...offering, thumbnailUrl: offering.imageUrl },
          priceConfigId: '',
        },
        closeDisclosure,
      );
    } else {
      goToConcierge(offering.identifier);
    }
  });

  const ConciergeDialog = useMemo(() => {
    if (!isConciergeServiceCategory(currentCategory)) return null;

    return (
      <ProductOfferingDialog
        onCustomizeOfferingClick={onCustomizeClick}
        category={currentCategory}
        isOpen={isOpen}
        onClose={closeDisclosure}
        selectedOffering={selectedOffering.selectedOffering}
        offeringSortIndex={selectedOffering.selectedOfferingSortIndex!}
      />
    );
  }, [currentCategory, isOpen, closeDisclosure, onCustomizeClick, selectedOffering]);

  return { ConciergeDialog, onCardSelect };
};
